/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */

/**
 * Adds attribute target="_blank" to all hyperlinks within `el`
 * to open those links in a new tab
 *
 * @param {HTMLElement|null} el This is a React ref to the story wrapper element
 */
function extractHostnames(hosts) {
  const hostList = Array.isArray(hosts) ? hosts : [hosts];
  const hostnames = new Set();
  hostList.forEach(host => {
    if (!host || typeof host !== "string") return;
    try {
      const hostname = (host.includes("://") ? new URL(host).hostname : host).replace(/^www\./, "");
      if (hostname) hostnames.add(hostname);
    } catch (e) {
      // ignore invalid host
    }
  });
  return hostnames;
}

/**
 * Adds attribute target="_blank" to all external hyperlinks within `el`
 * and converts internal host hyperlinks to relative paths.
 *
 * @param {HTMLElement|null} el This is a React ref to the story wrapper element
 * @param {string|string[]} internalHosts Backend host or array of internal hosts to treat as local
 */
export function addTargetBlankToExtLinks(el, internalHosts = "") {
  if (!el) return;
  const internalHostnames = extractHostnames(internalHosts);
  const currentHostname =
    typeof window !== "undefined" && window.location?.hostname
      ? window.location.hostname.replace(/^www\./, "")
      : "";

  const documentNodes = el.getElementsByTagName("a") || [];
  const anchors = Array.from(documentNodes);
  anchors.forEach(anchor => {
    if (!anchor.hostname) return;
    const normalizedAnchorHostname = anchor.hostname.replace(/^www\./, "");
    const isCurrentHost = currentHostname && normalizedAnchorHostname === currentHostname;
    const isInternalHost = internalHostnames.has(normalizedAnchorHostname);

    if (isInternalHost || isCurrentHost) {
      if (isInternalHost) {
        anchor.setAttribute("href", `${anchor.pathname}${anchor.search}${anchor.hash}`);
      }
      return;
    }

    if (anchor.getAttribute("target") !== "_self") {
      anchor.setAttribute("target", "_blank");
    }
  });
}

export function normalizeInternalLinks(el, internalHosts = "", selector = "a") {
  if (!el || !internalHosts) return;
  const internalHostnames = extractHostnames(internalHosts);
  if (internalHostnames.size === 0) return;

  Array.from(el.querySelectorAll(selector)).forEach(anchor => {
    if (!anchor.hostname) return;
    const anchorHostname = anchor.hostname.replace(/^www\./, "");
    if (internalHostnames.has(anchorHostname)) {
      anchor.setAttribute("href", `${anchor.pathname}${anchor.search}${anchor.hash}`);
    }
  });
}
